import * as React from 'react';
import { Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Bars3Icon } from '@heroicons/react/24/outline';

import { Drawer } from '../ui';
import { useAuth } from '../auth/AuthContext';
import { isInsurance } from '../auth/roles';
import { SidebarContent } from './Sidebar';
import { NotificationBell } from './NotificationBell';
import { LanguageSwitcher } from './LanguageSwitcher';
import { isRtl } from '../i18n';

/**
 * Top-level shell.
 *
 * Layout strategy:
 *   - The outer container is exactly viewport height with overflow-hidden.
 *   - The sidebar is full-height (h-screen) and fixed in place.
 *   - The main content column is the only thing that scrolls (overflow-y-auto).
 *
 * This stops the sidebar from stretching past the viewport on long pages,
 * and keeps the brand/nav/user-card always visible while the content scrolls.
 */
export default function MainLayout() {
	const { user } = useAuth();
	const { t, i18n } = useTranslation();
	const [mobileOpen, setMobileOpen] = React.useState(false);
	// In RTL the mobile drawer should slide in from the right (where the user
	// expects "primary nav" to be). The Drawer component only takes left/right
	// literals, so we flip explicitly rather than relying on logical CSS.
	const drawerSide: 'left' | 'right' = isRtl(i18n.resolvedLanguage) ? 'right' : 'left';

	// v0 notification audience: insurance roles + super admin. Suppliers and
	// mechanics will follow once we hook win/conversion notifications for
	// them. Hiding the bell rather than rendering an empty one — the
	// 30s polling has a (small) cost we don't want to pay for users who
	// can't see anything anyway.
	const showBell = !!user && (isInsurance(user.role) || user.role === 'SUPER_ADMIN');

	return (
		<div className="h-screen flex bg-ground overflow-hidden">
			{/* Sidebar — desktop. Pinned to viewport height, never scrolls with content. */}
			<aside className="hidden lg:flex lg:w-64 lg:shrink-0 h-screen">
				<SidebarContent />
			</aside>

			{/* Sidebar — mobile drawer. Side mirrors in RTL so the gesture/edge
			    feels natural in arabic. */}
			<Drawer
				open={mobileOpen}
				onClose={() => setMobileOpen(false)}
				side={drawerSide}
				className="!max-w-[18rem]"
			>
				<SidebarContent onNavigate={() => setMobileOpen(false)} />
			</Drawer>

			{/* Main column — scrolls independently of the sidebar. */}
			<div className="flex-1 min-w-0 flex flex-col h-screen">
				{/* Top bar.
				    - Mobile: hamburger + brand on the left, bell + avatar on the right.
				    - Desktop: empty space on the left (sidebar carries the brand),
				      bell + avatar on the right. Same component, different visibility
				      classes per breakpoint. */}
				<header className="flex-shrink-0 bg-white border-b border-slate-200">
					<div className="h-14 px-3 lg:px-6 flex items-center gap-3">
						<button
							onClick={() => setMobileOpen(true)}
							aria-label={t('navigation:openNavigation')}
							className="lg:hidden p-2 -ms-2 rounded-md hover:bg-slate-100 text-ink"
						>
							<Bars3Icon className="h-6 w-6" />
						</button>
						<div className="lg:hidden font-semibold tracking-tight text-ink">
							SPARTS <span className="font-light text-ink-muted">auto</span>
						</div>
						{/* `ms-auto` (margin-inline-start: auto) keeps the cluster
						    glued to the natural end-side of the bar in both LTR and RTL. */}
						<div className="ms-auto flex items-center gap-1 sm:gap-2">
							<LanguageSwitcher />
							{showBell && <NotificationBell />}
							{user && (
								<div className="h-8 w-8 rounded-full bg-brand-800 text-white grid place-items-center text-xs font-semibold">
									{user.name?.charAt(0).toUpperCase()}
								</div>
							)}
						</div>
					</div>
				</header>

				<main className="flex-1 min-w-0 overflow-y-auto">
					<Outlet />
				</main>
			</div>
		</div>
	);
}
